import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { fetchPastStreams } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Tv, 
  History, 
  ExternalLink, 
  Youtube, 
  Facebook, 
  Video,
  Calendar,
  PlayCircle
} from 'lucide-react';

interface PastVideo {
    id: number;
    title: string;
    date: string;
    thumbnail: string;
    videoUrl: string;
}

const Videos = () => {
    const [activeTab, setActiveTab] = useState<'live' | 'past'>('live');
    const [pastVideos, setPastVideos] = useState<PastVideo[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [youtubeLive, setYoutubeLive] = useState<boolean | null>(null);
    const { toast } = useToast();

    const YOUTUBE_API_KEY = 'AIzaSyDq7dD7vsFOxnllzngx1sH8IlrPAlF1sZU'; 
    const CHANNEL_ID = 'UC-Pspy21gS5ZX54wRRgalaA';

    useEffect(() => {
        if (activeTab === 'past') loadPastVideos();
        if (activeTab === 'live') checkYouTubeLive();
    }, [activeTab]);

    const loadPastVideos = async () => {
        setLoading(true);
        try {
            const response = await fetchPastStreams();
            const data = response.data.videos;
            const formatted: PastVideo[] = data.map((item: any) => ({
                id: item.id,
                title: item.title,
                date: item.created_at,
                thumbnail: item.thumbnail || 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?q=80&w=2070&auto=format&fit=crop',
                videoUrl: item.video_url,
            }));
            setPastVideos(formatted);
        } catch (err) {
            setError('Failed to load past videos.');
            toast({ title: 'Error', description: 'Unable to fetch past streams', variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    const checkYouTubeLive = async () => {
        try {
            const res = await fetch(
                `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${CHANNEL_ID}&type=video&eventType=live&key=${YOUTUBE_API_KEY}`
            );
            const data = await res.json();
            setYoutubeLive(data.items && data.items.length > 0);
        } catch (err) {
            setYoutubeLive(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#fcfaf7] py-20 px-4">
            <div className="max-w-7xl mx-auto">
                
                {/* Header */}
                <header className="max-w-3xl mx-auto text-center mb-16">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex justify-center mb-6"
                    >
                        <div className="p-3 bg-amber-100 rounded-2xl text-amber-700">
                            <Tv className="w-8 h-8" />
                        </div>
                    </motion.div>
                    
                    <motion.h1 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-slate-900 mb-6"
                    >
                        Broadcast Center
                    </motion.h1>
                    
                    <motion.p className="text-lg text-slate-600 font-serif mb-10">
                        Join our digital congregation and experience worship from anywhere in the world.
                    </motion.p>

                    {/* Styled Tab Switcher */}
                    <div className="inline-flex p-1.5 bg-slate-200/50 backdrop-blur-sm rounded-2xl border border-slate-200">
                        <button
                            onClick={() => setActiveTab('live')}
                            className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all duration-300 ${
                                activeTab === 'live' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            <span className={`w-2 h-2 rounded-full ${youtubeLive ? 'bg-red-500 animate-pulse' : 'bg-slate-400'}`} />
                            Live Now
                        </button>
                        <button
                            onClick={() => setActiveTab('past')}
                            className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all duration-300 ${
                                activeTab === 'past' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            <History className="w-4 h-4" />
                            Archives
                        </button>
                    </div>
                </header>

                <AnimatePresence mode="wait">
                    {activeTab === 'live' ? (
                        <motion.div 
                            key="live-panel"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
                        >
                            {/* Facebook Card */}
                            <LivePlatformCard 
                                icon={<Facebook className="w-6 h-6" />}
                                platform="Facebook"
                                url="https://www.facebook.com/dioceseofibadansouth/live_videos"
                                color="bg-blue-600"
                            />

                            {/* YouTube Card with Logic */}
                            <div className="relative group">
                                <div className="absolute inset-0 bg-white rounded-[2.5rem] shadow-md translate-x-2 translate-y-2 group-hover:translate-x-3 group-hover:translate-y-3 transition-transform duration-300 border border-slate-100" />
                                <Card className="relative z-10 overflow-hidden rounded-[2.5rem] border-none shadow-xl bg-white h-full">
                                    <CardContent className="p-0 flex flex-col h-full">
                                        {youtubeLive ? (
                                            <div className="aspect-video">
                                                <iframe
                                                    title="YouTube Live"
                                                    src={`https://www.youtube.com/embed/live_stream?channel=${CHANNEL_ID}&autoplay=1`}
                                                    className="w-full h-full"
                                                    allowFullScreen
                                                />
                                            </div>
                                        ) : (
                                            <div className="aspect-video bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
                                                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-red-600 mb-4">
                                                    <Youtube className="w-6 h-6" />
                                                </div>
                                                <p className="font-serif font-bold text-slate-800">YouTube Stream Offline</p>
                                                <a href={`https://www.youtube.com/@DioceseOfIbadanSouth`} target="_blank" className="text-xs text-blue-600 font-bold mt-2 uppercase tracking-widest">Visit Channel</a>
                                            </div>
                                        )}
                                        <div className="p-6 text-center border-t border-slate-50">
                                            <h3 className="font-serif font-bold text-slate-900">YouTube Broadcast</h3>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* TikTok Card */}
                            <LivePlatformCard 
                                icon={<Video className="w-6 h-6" />}
                                platform="TikTok"
                                url="https://www.tiktok.com/@ibadansouthangdiocese"
                                color="bg-slate-900"
                            />
                        </motion.div>
                    ) : (
                        <motion.div 
                            key="past-panel"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
                        >
                            {loading ? (
                                [1,2,3].map(n => <div key={n} className="h-80 bg-slate-200 rounded-[2.5rem] animate-pulse" />)
                            ) : pastVideos.map((video) => (
                                <div key={video.id} className="relative group">
                                    <div className="absolute inset-0 bg-white rounded-[2.5rem] shadow-md translate-x-1.5 translate-y-1.5 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform border border-slate-100" />
                                    <Card className="relative z-10 overflow-hidden rounded-[2.5rem] border-none shadow-xl bg-white">
                                        <CardContent className="p-0">
                                            <div className="aspect-video relative overflow-hidden">
                                                <iframe
                                                    src={video.videoUrl}
                                                    title={video.title}
                                                    className="w-full h-full"
                                                    allowFullScreen
                                                />
                                            </div>
                                            <div className="p-6">
                                                <div className="flex items-center gap-2 text-amber-600 mb-2">
                                                    <Calendar className="w-3.5 h-3.5" />
                                                    <span className="text-[10px] font-bold uppercase tracking-widest">
                                                        {new Date(video.date).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                <h3 className="text-lg font-serif font-bold text-slate-900 line-clamp-2">
                                                    {video.title}
                                                </h3>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

// Sub-component for Live Platform Links
const LivePlatformCard = ({ icon, platform, url, color }: any) => (
    <div className="relative group">
        <div className="absolute inset-0 bg-white rounded-[2.5rem] shadow-md translate-x-2 translate-y-2 group-hover:translate-x-3 group-hover:translate-y-3 transition-transform duration-300 border border-slate-100" />
        <Card className="relative z-10 overflow-hidden rounded-[2.5rem] border-none shadow-xl bg-white h-full">
            <CardContent className="p-0 flex flex-col h-full">
                <div className={`aspect-video ${color} flex items-center justify-center text-white relative`}>
                    {icon}
                    <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-md rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest border border-white/30">
                        Live Link
                    </div>
                </div>
                <div className="p-8 text-center flex-1 flex flex-col justify-between">
                    <div>
                        <h3 className="text-xl font-serif font-bold text-slate-900 mb-2">{platform}</h3>
                        <p className="text-sm text-slate-500 font-serif mb-6">Connect with us on our official {platform} page.</p>
                    </div>
                    <a 
                        href={url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 bg-slate-900 text-white py-4 rounded-2xl font-bold text-xs uppercase tracking-widest hover:bg-amber-600 transition-colors"
                    >
                        Join Broadcast <ExternalLink className="w-4 h-4" />
                    </a>
                </div>
            </CardContent>
        </Card>
    </div>
);

export default Videos;